import React from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, Ban, UserCheck, Search, Gem } from "lucide-react";
import FoundingParentBadge from "@/components/site/FoundingParentBadge";

const formatJoinDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};
const formatTimeAgo = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  const sec = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (sec < 60) return "Baru saja";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} menit yang lalu`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} jam yang lalu`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day} hari yang lalu`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo} bulan yang lalu`;
  return `${Math.floor(day / 365)} tahun yang lalu`;
};

/**
 * Pengguna — daftar semua pengguna terdaftar (super admin only).
 */
export default function UsersTab({
  user,
  sortedAllUsers,
  usersQuery,
  setUsersQuery,
  usersRoleFilter,
  setUsersRoleFilter,
  usersBannedFilter,
  setUsersBannedFilter,
  usersSortBy,
  setUsersSortBy,
  loadAllUsers,
  setDetailUserId,
  openBanDialog,
  unbanUser,
  toggleFoundingParent,
}) {
  return (
    <TabsContent value="all-users" className="mt-6" data-testid="all-users-panel">
      <p className="text-sm text-[#4B5563] mb-4">
        Semua pengguna terdaftar. Nonaktifkan (ban) akun yang melanggar aturan — pengguna yang dinonaktifkan tidak bisa login atau memposting, namun tetap bisa menjelajah sebagai tamu.
      </p>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="flex-1 min-w-[220px]">
          <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1">Cari email/nama</div>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4B5563]" />
            <Input value={usersQuery} onChange={(e) => setUsersQuery(e.target.value)} placeholder="mis: parent@demo" data-testid="users-search" className="pl-9" />
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1">Peran</div>
          <Select value={usersRoleFilter} onValueChange={setUsersRoleFilter}>
            <SelectTrigger data-testid="users-role-filter" className="w-40 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua peran</SelectItem>
              <SelectItem value="parent">Parent</SelectItem>
              <SelectItem value="moderator">Moderator</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="superadmin">Super Admin</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1">Status</div>
          <Select value={usersBannedFilter} onValueChange={setUsersBannedFilter}>
            <SelectTrigger data-testid="users-status-filter" className="w-40 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua status</SelectItem>
              <SelectItem value="active">Aktif</SelectItem>
              <SelectItem value="banned">Dinonaktifkan</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={loadAllUsers} data-testid="users-apply-filter" className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white h-9">Terapkan</Button>
        {user?.role === "superadmin" && (
          <Button
            onClick={() => {
              const token = localStorage.getItem("token");
              const link = document.createElement("a");
              link.href = `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/export.csv`;
              fetch(link.href, { headers: { Authorization: `Bearer ${token}` } })
                .then((r) => r.blob())
                .then((blob) => {
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url; a.download = `tsr-users-${new Date().toISOString().slice(0, 10)}.csv`;
                  document.body.appendChild(a); a.click(); a.remove();
                  URL.revokeObjectURL(url);
                }).catch(() => toast.error("Gagal mengunduh CSV"));
            }}
            data-testid="users-export-csv"
            className="rounded-full bg-[#D2673E] hover:bg-[#C25730] text-white h-9"
          >
            Export CSV
          </Button>
        )}
        <div className="ml-auto">
          <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1">Urutkan</div>
          <Select value={usersSortBy} onValueChange={setUsersSortBy}>
            <SelectTrigger data-testid="users-sort-select" className="w-52 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="last_active">Aktivitas terakhir (baru→lama)</SelectItem>
              <SelectItem value="joined">Bergabung (baru→lama)</SelectItem>
              <SelectItem value="joined_asc">Bergabung (lama→baru)</SelectItem>
              <SelectItem value="contribution">Kontribusi terbanyak</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="bg-white border border-[#EAE7E0] rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F3F1ED] text-xs uppercase tracking-widest text-[#4B5563]">
            <tr>
              <th className="text-left p-3">Pengguna</th>
              <th className="text-left p-3 w-28">Peran</th>
              <th className="text-left p-3 w-28">Bergabung</th>
              <th className="text-left p-3 w-32">Aktivitas Terakhir</th>
              <th className="text-right p-3 w-32">Kontribusi</th>
              <th className="text-left p-3 w-28">Status</th>
              <th className="text-right p-3 w-32">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {sortedAllUsers.map((u, i) => {
              const isSelf = u.user_id === user.user_id;
              const isBanned = !!u.banned;
              let banUntilText = "";
              if (isBanned && u.banned_until) {
                const d = new Date(u.banned_until);
                banUntilText = `sampai ${d.toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" })}`;
              } else if (isBanned) {
                banUntilText = "permanen";
              }
              return (
                <tr key={u.user_id} data-testid={`user-row-${i}`} className={"border-t border-[#EAE7E0] hover:bg-[#F9F8F6] transition-colors " + (isBanned ? "bg-[#FEF2F2]/40" : "")}>
                  <td className="p-3">
                    <button
                      type="button"
                      onClick={() => setDetailUserId(u.user_id)}
                      data-testid={`user-open-detail-${i}`}
                      className="text-left group"
                      title="Lihat detail pengguna"
                    >
                      <div className="text-[#1F2937] font-medium group-hover:text-[#D2673E] transition-colors">{u.name}</div>
                      <div className="text-xs text-[#4B5563]">{u.email}</div>
                    </button>
                    {u.is_founding_parent && (
                      <div className="mt-1"><FoundingParentBadge testid={`user-founding-badge-${i}`} /></div>
                    )}
                    {isBanned && u.ban_reason && <div className="text-[11px] text-[#B91C1C] italic mt-0.5">Alasan: {u.ban_reason}</div>}
                  </td>
                  <td className="p-3 text-[#4B5563]"><span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-[#EAE7E0] text-[#1F2937]">{u.role}</span></td>
                  <td className="p-3 text-xs text-[#4B5563] tabular-nums" data-testid={`user-joined-${i}`}>{formatJoinDate(u.created_at)}</td>
                  <td className="p-3 text-xs text-[#4B5563]" data-testid={`user-last-active-${i}`}>{formatTimeAgo(u.last_active)}</td>
                  <td className="p-3 text-right text-[11px] text-[#4B5563]">
                    <div>{u.thread_count} thread</div>
                    <div>{u.reply_count} balasan</div>
                    <div>{u.review_count} review</div>
                  </td>
                  <td className="p-3">
                    {isBanned ? (
                      <div>
                        <span data-testid={`user-status-${i}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-[#FEE2E2] text-[#B91C1C] border border-[#FCA5A5]">
                          <Ban size={10} /> Dinonaktifkan
                        </span>
                        <div className="text-[10px] text-[#4B5563] mt-1">{banUntilText}</div>
                      </div>
                    ) : (
                      <span data-testid={`user-status-${i}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-[#DCFCE7] text-[#166534] border border-[#BBF7D0]">
                        <UserCheck size={10} /> Aktif
                      </span>
                    )}
                  </td>
                  <td className="p-3 text-right">
                    {isSelf ? (
                      <span className="text-[11px] text-[#4B5563] italic">akun Anda</span>
                    ) : (
                      <div className="inline-flex items-center gap-1.5 flex-wrap justify-end">
                        <Button
                          variant="outline"
                          onClick={() => toggleFoundingParent && toggleFoundingParent(u)}
                          data-testid={`user-toggle-founding-${i}`}
                          title={u.is_founding_parent ? "Cabut status Founding Parent" : "Jadikan Founding Parent"}
                          className={
                            "rounded-full h-8 px-3 text-[11px] " +
                            (u.is_founding_parent
                              ? "border-[#D9BF7A] text-[#8B5E17] bg-[#F4E7BE] hover:bg-[#EEDFA9]"
                              : "border-[#EAE7E0] text-[#4B5563] hover:border-[#D9BF7A] hover:text-[#8B5E17]")
                          }
                        >
                          <Gem size={12} className="mr-1" />
                          {u.is_founding_parent ? "Founding" : "Set Founding"}
                        </Button>
                        {isBanned ? (
                          <Button variant="outline" onClick={() => unbanUser(u)} data-testid={`user-unban-${i}`} className="rounded-full border-[#166534] text-[#166534] h-8">
                            <UserCheck size={12} className="mr-1" /> Aktifkan
                          </Button>
                        ) : (
                          <Button variant="outline" onClick={() => openBanDialog(u)} data-testid={`user-ban-${i}`} className="rounded-full border-[#B91C1C] text-[#B91C1C] h-8">
                            <Ban size={12} className="mr-1" /> Ban
                          </Button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
            {sortedAllUsers.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-[#4B5563]">Tidak ada pengguna cocok filter.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="text-xs text-[#4B5563] mt-3">Total ditampilkan: {sortedAllUsers.length} pengguna.</div>
    </TabsContent>
  );
}

// Re-export helpers so parent can use the same formatting if needed elsewhere
export { formatJoinDate, formatTimeAgo };
